'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion, Variants } from 'motion/react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@natu/ui/Button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@natu/ui/Form';
import { Loader } from '@natu/ui/Loader';

import { coreWebVitalsFormSchema } from './coreWebVitalsFormSchema';

interface CoreWebVitalsFormFormProps {
  errorMessage?: string;
  testLengthInfo?: string;
  isPending: boolean;
  isError: boolean;
  onSubmit: (values: z.infer<typeof coreWebVitalsFormSchema>) => Promise<void>;
}

const errorVariants: Variants = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto' },
  exit: { opacity: 0, height: 0 },
};

export const CoreWebVitalsForm = ({
  isError,
  isPending,
  onSubmit,
  errorMessage,
  testLengthInfo,
}: CoreWebVitalsFormFormProps) => {
  const form = useForm<z.infer<typeof coreWebVitalsFormSchema>>({
    resolver: zodResolver(coreWebVitalsFormSchema),
    defaultValues: {
      url: '',
    },
  });

  const urlValue = form.watch('url');

  return (
    <div className="flex flex-col gap-y-4">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-y-4 lg:flex-row lg:gap-x-2"
        >
          <FormField
            control={form.control}
            name="url"
            render={({ field }) => (
              <FormItem className="relative w-full lg:max-h-12">
                <FormLabel className="sr-only">URL</FormLabel>
                <FormControl>
                  <Input
                    className="!mt-0 h-full w-full rounded-lg border border-gray-300 px-3.5 py-2.5"
                    placeholder="https://example.com"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            disabled={isPending || urlValue === ''}
            type="submit"
            variant="primary"
            size="xl"
            className="flex items-center gap-x-1 disabled:cursor-not-allowed lg:min-w-36"
          >
            RUN TEST
            <AnimatePresence mode="wait">{isPending ? <Loader /> : null}</AnimatePresence>
          </Button>
        </form>
      </Form>
      <AnimatePresence mode="wait">
        {isPending && (
          <motion.p
            className="text-gray-600"
            variants={errorVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {testLengthInfo ||
              'Don&apos;t worry if the test seems to be stuck. It usually takes 15 to 45 seconds.'}
          </motion.p>
        )}
      </AnimatePresence>
      <AnimatePresence mode="wait">
        {isError && (
          <motion.p
            className="text-error-500 overflow-hidden"
            variants={errorVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {errorMessage || 'Performance score check failed, please try again later'}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};
